import { getErrorMessage } from '@/api/errors'
import { ADO_WIKI_CONNECTOR_TYPES } from '@/components/projects/connectors/connectorConstants'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { useAzureDevOpsWikiPages, useAzureDevOpsWikiProjects, useAzureDevOpsWikis, useConnectors, useUploadContextWikiPages } from '@/hooks'
import { extractValidationMessage } from '@/lib/validationErrors'
import { cn } from '@/lib/utils'
import type { WikiPageNode } from '@/types'
import { MenuBookIcon } from '@/components/icons'
import { ChevronDown, Plus, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

type WikiPageUploadItem = {
  id: string
  pagePath: string
  includeSubPages: boolean
  description: string
}

function flattenPagePaths(root: WikiPageNode | undefined): string[] {
  if (!root) return []
  const paths: string[] = []
  const walk = (node: WikiPageNode) => {
    if (node.path) paths.push(node.path)
    node.subPages?.forEach(walk)
  }
  walk(root)
  return paths
}

function getPagePickerPlaceholder(selectedCount: number, hasWiki: boolean, isLoading: boolean, availableCount: number): string {
  if (selectedCount > 0) return 'Add more...'
  if (!hasWiki) return 'Select wiki first'
  if (isLoading) return 'Loading pages...'
  if (availableCount === 0) return 'No pages available'
  return 'Search and select pages'
}

interface WikiPagesUploadDialogProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly projectId: string
}

export function WikiPagesUploadDialog({ open, onOpenChange, projectId }: WikiPagesUploadDialogProps) {
  const [selectedConnectorId, setSelectedConnectorId] = useState<string>('')
  const [adoProject, setAdoProject] = useState<string>('')
  const [wikiId, setWikiId] = useState<string>('')
  const [pageSearch, setPageSearch] = useState<string>('')
  const [pendingIncludeSubPages, setPendingIncludeSubPages] = useState<boolean>(false)
  const [items, setItems] = useState<WikiPageUploadItem[]>([])

  const { data: connectors = [] } = useConnectors(projectId)

  const connectorOptions = useMemo(
    () => connectors.filter((c) => ADO_WIKI_CONNECTOR_TYPES.includes(c.type)),
    [connectors]
  )

  const connectorId =
    selectedConnectorId && connectorOptions.some((c) => c.id === selectedConnectorId)
      ? selectedConnectorId
      : (connectorOptions.length === 1 ? connectorOptions[0].id : '')

  const { data: adoProjects = [] } = useAzureDevOpsWikiProjects(projectId, Boolean(connectorId))
  const { data: wikis = [] } = useAzureDevOpsWikis(projectId, adoProject, Boolean(adoProject))
  const { data: pageTree, isLoading: isPageTreeLoading } = useAzureDevOpsWikiPages(projectId, adoProject, wikiId, Boolean(wikiId))
  const uploadWikiMutation = useUploadContextWikiPages(projectId)

  const wikiName = useMemo(() => wikis.find((w) => w.id === wikiId)?.name ?? '', [wikis, wikiId])
  const availablePaths = useMemo(() => flattenPagePaths(pageTree), [pageTree])
  const filteredPaths = useMemo(
    () => availablePaths.filter((path) => path.toLowerCase().includes(pageSearch.toLowerCase())),
    [availablePaths, pageSearch]
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedConnectorId('')
      setAdoProject('')
      setWikiId('')
      setPageSearch('')
      setPendingIncludeSubPages(false)
      setItems([])
    }
    onOpenChange(nextOpen)
  }

  const handleSelectPage = (path: string) => {
    if (items.some((item) => item.pagePath === path && item.includeSubPages === pendingIncludeSubPages)) {
      toast.error('This page is already added.')
      return
    }
    setItems((prev) => [
      ...prev,
      {
        id: `${path}-${pendingIncludeSubPages}-${prev.length}`,
        pagePath: path,
        includeSubPages: pendingIncludeSubPages,
        description: '',
      },
    ])
  }

  const canUpload = items.length > 0

  const handleUpload = async () => {
    if (!canUpload) return

    try {
      const response = await uploadWikiMutation.mutateAsync({
        pages: items.map((item) => ({
          connectorId,
          azureDevOpsProject: adoProject,
          wikiId,
          wikiName,
          pagePath: item.pagePath,
          pageUrl: null,
          includeSubPages: item.includeSubPages,
          description: item.description,
        })),
      })
      toast.success('Uploaded successfully')
      if (response.skippedCount > 0) {
        toast.info(
          `${response.skippedCount} ${response.skippedCount === 1 ? 'wiki page was' : 'wiki pages were'} already in context and ${response.skippedCount === 1 ? 'was' : 'were'} skipped.`
        )
      }
      handleOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Upload failed')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>Add Wiki Pages</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-sm font-medium">Connector *</Label>
            <Select
              value={connectorId}
              disabled={connectorOptions.length === 1}
              onValueChange={(id) => {
                setSelectedConnectorId(id)
                setAdoProject('')
                setWikiId('')
                setPageSearch('')
                setItems([])
              }}
            >
              <SelectTrigger className="mt-2" aria-label="Select connector">
                <SelectValue placeholder="Select connector" />
              </SelectTrigger>
              <SelectContent>
                {connectorOptions.map((connector) => (
                  <SelectItem key={connector.id} value={connector.id}>
                    {connector.type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium">Project *</Label>
            <Select
              value={adoProject}
              onValueChange={(project) => {
                setAdoProject(project)
                setWikiId('')
                setPageSearch('')
                setItems([])
              }}
              disabled={!connectorId}
            >
              <SelectTrigger className="mt-2" aria-label="Select Azure DevOps project">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {adoProjects.map((p) => (
                  <SelectItem key={p.id} value={p.name}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium">Wiki *</Label>
            <Select
              value={wikiId}
              onValueChange={(id) => {
                setWikiId(id)
                setPageSearch('')
                setItems([])
              }}
              disabled={!adoProject}
            >
              <SelectTrigger className="mt-2" aria-label="Select wiki">
                <SelectValue placeholder="Select wiki" />
              </SelectTrigger>
              <SelectContent>
                {wikis.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-sm font-medium">Page *</Label>
            <div className="mt-2 flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={!wikiId || availablePaths.length === 0}>
                  <div
                    role="combobox"
                    aria-label="Select wiki page"
                    tabIndex={!wikiId || availablePaths.length === 0 ? -1 : 0}
                    className={cn(
                      'flex min-h-10 flex-1 flex-wrap items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 cursor-pointer',
                      (!wikiId || availablePaths.length === 0) && 'cursor-not-allowed opacity-50'
                    )}
                  >
                    {items.length === 0 && (
                      <MenuBookIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    {items.map((item) => (
                      <span key={item.id} className="flex items-center gap-1 rounded-full border bg-secondary px-2.5 py-0.5 text-sm">
                        {item.pagePath}{item.includeSubPages ? ' (+ sub-pages)' : ''}
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label={`Remove ${item.pagePath}`}
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
                          }}
                          onClick={() => setItems((prev) => prev.filter((entry) => entry.id !== item.id))}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      {getPagePickerPlaceholder(items.length, Boolean(wikiId), isPageTreeLoading, availablePaths.length)}
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] p-0">
                  <div className="relative border-b p-2">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={pageSearch}
                      onChange={(e) => setPageSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="Search and select pages"
                      className="pl-8"
                      aria-label="Search and select pages"
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {filteredPaths.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No pages found.</div>
                    ) : (
                      filteredPaths.map((path) => (
                        <DropdownMenuItem
                          key={path}
                          onSelect={(e) => e.preventDefault()}
                          onClick={() => handleSelectPage(path)}
                          className="flex items-center gap-2"
                        >
                          {path}
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <label className="flex h-10 items-center gap-2 text-sm">
            <Checkbox
              checked={pendingIncludeSubPages}
              onCheckedChange={(checked) => setPendingIncludeSubPages(checked === true)}
              aria-label="Include sub-pages"
            />
            Include sub-pages
          </label>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} data-testid="wiki-page-row" className="grid grid-cols-[1fr_1fr_36px] items-center gap-3 rounded-lg border p-3">
              <div className="flex min-w-0 items-center gap-2">
                <div className="shrink-0 rounded border p-2 text-muted-foreground">
                  <MenuBookIcon className="h-4 w-4" />
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="truncate text-sm font-medium">
                        {item.pagePath}{item.includeSubPages ? ' (+ sub-pages)' : ''}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{item.pagePath}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                placeholder="Description (Optional)"
                value={item.description}
                data-testid="wiki-page-description"
                aria-label={`Description for ${item.pagePath}`}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((entry) =>
                      entry.id === item.id ? { ...entry, description: e.target.value } : entry
                    )
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${item.pagePath}`}
                onClick={() => setItems((prev) => prev.filter((entry) => entry.id !== item.id))}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!canUpload || uploadWikiMutation.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
